// WorkspaceCallPanel — THE shell call panel (comms-phone v2, "calls inside the
// workspace"; Nika 2026-10-01). Mounted ONCE at the shell root (app.js), so a
// call survives every route change, like MeetCallWidget's PiP dock.
//
// DATA: one root key, `_callPanel` (null = idle = hidden), written ONLY by
// functions/calls/workspaceCall.js from the SDK voice client's events (the
// per-member call stream + the call handle). `_callPanelTick` re-renders the
// in-call timer once a second while a call is answered.
//
// STATES (`_callPanel.phase`): outgoing (calling / ringing) · incoming
// (accept / decline) · active (timer, mute, hold, keypad, hang up) · ended
// (the duration, or a missed call with the bell glyph). Hold and keypad show
// only when the call handle offers them (`canHold` / `canKeypad`).
//
// CHROME: the toast island's own look (GlobalNotification: the `dialog`
// surface on the `float` elevation), docked bottom-right on the composer
// stack's line (`--stack-line`: the composer's top and its docked rows + the
// gap) so it never covers the composer or the bottom-centre toast stack.
// Above the credit gate's scrim (10300) so a ringing call can always be
// answered; below the toast stack. ZERO-SHIFT: position fixed, the
// shell-root wrapper is `display: contents` (the CallInvitationBanner rule).
//
// A11Y: role=dialog with a name; the phase line is a live region (assertive
// while ringing). On a ring, focus moves into the card (scope.manageFocus,
// the CreditBlocker technique) and returns when the panel closes. Escape asks
// first, then hangs up or declines (requestEndWorkspaceCall); on an ended call
// it dismisses. Escape acts only while focus is inside the card.
export const WorkspaceCallPanel = {
  tag: 'div',
  display: 'contents',
  subscribeTo: ['_callPanel', '_callPanelTick'],

  scope: {
    manageFocus(el, ringKey) {
      if (typeof document === 'undefined') return
      if (ringKey === el._callFocusKey) return
      el._callFocusKey = ringKey
      // design-d2: never take focus from a field the member is typing in;
      // the ring is announced (assertive phase line) and Tab reaches the card.
      if (ringKey && !el.call('workspaceCallRingMayTakeFocus', document.activeElement)) return
      if (ringKey) {
        el._returnFocusTo = document.activeElement
        // A bounded poll of tasks, not one frame (the focusContextMenu shape):
        // the card is still `display: none` until its `hide` effect paints,
        // and a background tab never runs requestAnimationFrame. Measured live:
        // one timeout lost the first ring after a page load.
        let tries = 0
        const tick = () => {
          if (el._callFocusKey !== ringKey || !el.node) return
          if (typeof el.node.focus === 'function') el.node.focus()
          if (document.activeElement !== el.node && ++tries < 12) setTimeout(tick, 50)
        }
        setTimeout(tick, 0)
      } else if (el._returnFocusTo) {
        if (el._returnFocusTo.isConnected && el._returnFocusTo.focus) el._returnFocusTo.focus()
        el._returnFocusTo = null
      }
    },
    // `--call-clear` on the root while the card shows: its height + the gap
    // to the toast; removed while hidden (the toast's fallback is 0%).
    // The shell's toast stack docks on --stack-line and holds --call-clear
    // under its toasts (design-d2 35bf386a1; app.js GlobalNotification), so a
    // toast never covers Accept / Decline. offsetHeight, not the rect: the
    // entry animation translates the card (measured live).
    // Read after the card paints (a task: the hide effect lands first).
    syncClear(el, shown) {
      if (typeof document === 'undefined') return
      setTimeout(() => {
        const node = el.node
        const value = el.call(
          'workspaceCallClear',
          shown && node && node.offsetHeight ? { height: node.offsetHeight } : null
        )
        const style = document.documentElement.style
        if (value) style.setProperty('--call-clear', value)
        else style.removeProperty('--call-clear')
      }, 0)
    }
  },

  Card: {
    hide: (el, s) => {
      const panel = (s.root || s)._callPanel
      el.scope.manageFocus(el, panel && panel.phase === 'incoming' ? panel.callId || 'ring' : null)
      el.scope.syncClear(el, !!panel)
      return !panel
    },
    onKeydown: (e, el) => {
      if (e.key !== 'Escape') return
      e.stopPropagation()
      el.call('requestEndWorkspaceCall')
    },

    position: 'fixed',
    bottom: 'var(--stack-line, var(--spacing-C1))',
    right: 'A',
    zIndex: '10400',
    theme: 'dialog',
    shadow: 'float',
    round: 'radiusSheet',
    flow: 'y',
    gap: 'Z1',
    padding: 'A',
    width: 'G1',
    maxWidth: 'calc(100dvw - var(--spacing-B))',
    boxSizing: 'border-box',
    animationName: 'fadeInUpShort',
    animationDuration: 'C',
    animationTimingFunction: 'springBezier',
    animationFillMode: 'both',
    role: 'dialog',
    tabindex: '-1',
    outline: 'none',
    'aria-label': (el) => el.call('polyglot', 'workspace.callPanel.aria'),
    // The phase line and the caller (design-d2). One panel per shell, so
    // the two ids are unique.
    ariaDescribedby: 'workspaceCallPanelPhase workspaceCallPanelTitle',
    '@mobileL': {
      left: 'Z',
      right: 'Z',
      width: 'auto',
      maxWidth: 'none'
    },

    Header: {
      flow: 'x',
      align: 'center space-between',
      gap: 'Z1',

      Lead: {
        flow: 'x',
        align: 'center',
        gap: 'Z',
        minWidth: '0',

        Glyph: {
          align: 'center center',
          boxSize: 'controlH',
          minWidth: 'controlH',
          // A tint of its own ink, not the retired `layer` theme (design-d2):
          // the ground follows the ink per scheme (Rule 67).
          round: 'radiusPill',
          background: 'color-mix(in srgb, currentColor 14%, var(--color-tintBase))',
          color: (el, s) => {
            const panel = (s.root || s)._callPanel
            return el.call('workspaceCallMissed', panel) ? 'dangerInk' : 'title'
          },
          Icon: {
            name: (el, s) =>
              el.call('workspaceCallMissed', (s.root || s)._callPanel) ? 'bell' : 'phone',
            boxSize: 'iconSm'
          }
        },

        Text: {
          flow: 'y',
          gap: 'W',
          minWidth: '0',

          // The house eyebrow (design-d2): AppMicroLabel.
          Eyebrow: {
            extends: 'AppMicroLabel',
            id: 'workspaceCallPanelPhase',
            role: 'status',
            ariaLive: (el, s) => {
              const panel = (s.root || s)._callPanel
              return panel && panel.phase === 'incoming' ? 'assertive' : 'polite'
            },
            text: (el, s) => el.call('workspaceCallEyebrow', (s.root || s)._callPanel)
          },

          Title: {
            tag: 'p',
            id: 'workspaceCallPanelTitle',
            margin: '0',
            fontSize: 'A',
            fontWeight: '700',
            color: 'title',
            lineHeight: '1.3',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            text: (el, s) => el.call('workspaceCallTitle', (s.root || s)._callPanel)
          },

          Meta: {
            tag: 'span',
            fontSize: 'Z2',
            color: 'caption',
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            hide: (el, s) => !el.call('workspaceCallMeta', (s.root || s)._callPanel),
            text: (el, s) => el.call('workspaceCallMeta', (s.root || s)._callPanel)
          }
        }
      },

      // THE house close X — only on an ended call (a live call ends with the
      // hang-up control, never with an X).
      CloseButton: {
        hide: (el, s) => {
          const panel = (s.root || s)._callPanel
          return !panel || panel.phase !== 'ended'
        },
        title: (el) => el.call('polyglot', 'workspace.callPanel.action.dismiss'),
        onClick: (e, el) => {
          e.stopPropagation()
          el.call('dismissWorkspaceCall')
        }
      }
    },

    // Question above its two answers: Georgian labels do not fit beside the
    // question at the dock width (measured live), so the answers get their own
    // row and wrap rather than overflow.
    Confirm: {
      flow: 'y',
      gap: 'Z',
      hide: (el, s) => {
        const panel = (s.root || s)._callPanel
        return !panel || !panel.confirmEnd
      },
      Question: {
        tag: 'span',
        fontSize: 'Z2',
        color: 'title',
        text: (el, s) => {
          const panel = (s.root || s)._callPanel
          return el.call(
            'polyglot',
            panel && panel.phase === 'incoming'
              ? 'workspace.callPanel.confirm.decline'
              : 'workspace.callPanel.confirm.end'
          )
        }
      },
      Choices: {
        flow: 'x',
        align: 'center flex-end',
        flexWrap: 'wrap',
        gap: 'Y',
        // The house tonal secondary (design-d2), not the retired `layer`.
        Keep: {
          extends: 'CancelButton',
          type: 'button',
          text: '{{ workspace.callPanel.confirm.keep | polyglot }}',
          onClick: (e, el) => el.call('cancelEndWorkspaceCall')
        },
        // Two capsules, not one with a reactive label: a Button's `text` is
        // read once (measured live), and the template form keeps each label
        // static and reactive to the language.
        End: {
          extends: 'WorkspaceCallAction',
          theme: 'error',
          hide: (el, s) => ((s.root || s)._callPanel || {}).phase === 'incoming',
          text: '{{ workspace.callPanel.confirm.yes | polyglot }}',
          onClick: (e, el) => el.call('requestEndWorkspaceCall')
        },
        DeclineCall: {
          extends: 'WorkspaceCallAction',
          theme: 'error',
          hide: (el, s) => ((s.root || s)._callPanel || {}).phase !== 'incoming',
          text: '{{ workspace.callPanel.action.decline | polyglot }}',
          onClick: (e, el) => el.call('requestEndWorkspaceCall')
        }
      }
    },

    Keypad: {
      hide: (el, s) => {
        const panel = (s.root || s)._callPanel
        return !panel || !panel.keypadOpen
      },
      flow: 'y',
      gap: 'Y',
      role: 'group',
      'aria-label': (el) => el.call('polyglot', 'workspace.callPanel.keypad'),
      Digits: {
        tag: 'span',
        fontSize: 'A',
        color: 'title',
        textAlign: 'center',
        minHeight: 'A',
        fontVariantNumeric: 'tabular-nums',
        letterSpacing: '.1em',
        text: (el, s) => ((s.root || s)._callPanel || {}).digits || ''
      },
      Keys: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 'Y',
        justifyItems: 'center',
        childExtends: 'IconButton',
        childrenAs: 'state',
        children: () => [
          { digit: '1' },
          { digit: '2' },
          { digit: '3' },
          { digit: '4' },
          { digit: '5' },
          { digit: '6' },
          { digit: '7' },
          { digit: '8' },
          { digit: '9' },
          { digit: '*' },
          { digit: '0' },
          { digit: '#' }
        ],
        childProps: {
          large: true,
          round: '50%',
          fontSize: 'A',
          fontVariantNumeric: 'tabular-nums',
          text: (el, s) => s.digit,
          onClick: (e, el, s) => el.call('pressWorkspaceCallDigit', s.digit)
        }
      }
    },

    Problem: {
      tag: 'span',
      fontSize: 'Z2',
      color: 'dangerInk',
      role: 'alert',
      hide: (el, s) => !((s.root || s)._callPanel || {}).errorKey,
      text: (el, s) => {
        const panel = (s.root || s)._callPanel
        return panel && panel.errorKey ? el.call('polyglot', panel.errorKey) : ''
      }
    },

    Controls: {
      flow: 'x',
      align: 'center flex-end',
      gap: 'Z',
      hide: (el, s) => {
        const panel = (s.root || s)._callPanel
        return !panel || panel.phase === 'ended' || panel.confirmEnd
      },

      Mute: {
        extends: 'IconButton',
        large: true,
        round: '50%',
        hide: (el, s) => {
          const panel = (s.root || s)._callPanel || {}
          return panel.phase !== 'active' || !panel.canMute
        },
        icon: (el, s) => (((s.root || s)._callPanel || {}).muted ? 'micOff' : 'mic'),
        'aria-pressed': (el, s) => String(!!((s.root || s)._callPanel || {}).muted),
        // A FIXED name; aria-pressed says the state (design-d2).
        'aria-label': (el) => el.call('polyglot', 'workspace.callPanel.action.mute'),
        title: (el) => el.call('polyglot', 'workspace.callPanel.action.mute'),
        onClick: (e, el) => el.call('toggleWorkspaceCallMute')
      },

      Hold: {
        extends: 'IconButton',
        large: true,
        round: '50%',
        icon: 'pause',
        hide: (el, s) => {
          const panel = (s.root || s)._callPanel || {}
          return panel.phase !== 'active' || !panel.canHold
        },
        'aria-pressed': (el, s) => String(!!((s.root || s)._callPanel || {}).held),
        'aria-label': (el) => el.call('polyglot', 'workspace.callPanel.action.hold'),
        title: (el) => el.call('polyglot', 'workspace.callPanel.action.hold'),
        onClick: (e, el) => el.call('toggleWorkspaceCallHold')
      },

      KeypadToggle: {
        extends: 'IconButton',
        large: true,
        round: '50%',
        icon: 'grid',
        hide: (el, s) => {
          const panel = (s.root || s)._callPanel || {}
          return panel.phase !== 'active' || !panel.canKeypad
        },
        'aria-expanded': (el, s) => String(!!((s.root || s)._callPanel || {}).keypadOpen),
        title: (el) => el.call('polyglot', 'workspace.callPanel.action.keypad'),
        onClick: (e, el) => el.call('toggleWorkspaceCallKeypad')
      },

      Decline: {
        extends: 'WorkspaceCallAction',
        theme: 'error',
        icon: 'phone',
        hide: (el, s) => ((s.root || s)._callPanel || {}).phase !== 'incoming',
        Icon: { transform: 'rotate(135deg)' },
        Label: { tag: 'span', text: '{{ workspace.callPanel.action.decline | polyglot }}' },
        onClick: (e, el) => el.call('declineWorkspaceCall')
      },

      Accept: {
        extends: 'WorkspaceCallAction',
        theme: 'success',
        icon: 'phone',
        hide: (el, s) => ((s.root || s)._callPanel || {}).phase !== 'incoming',
        disabled: (el, s) => !!((s.root || s)._callPanel || {}).busy,
        Label: { tag: 'span', text: '{{ workspace.callPanel.action.accept | polyglot }}' },
        onClick: (e, el) => el.call('acceptWorkspaceCall')
      },

      HangUp: {
        extends: 'IconButton',
        large: true,
        round: '50%',
        theme: 'error',
        icon: 'phone',
        hide: (el, s) => {
          const phase = ((s.root || s)._callPanel || {}).phase
          return phase !== 'active' && phase !== 'outgoing'
        },
        disabled: (el, s) => !!((s.root || s)._callPanel || {}).busy,
        title: (el) => el.call('polyglot', 'workspace.callPanel.action.hangUp'),
        Icon: { transform: 'rotate(135deg)' },
        ':hover': { filter: 'brightness(1.08)' },
        ':active': { transform: 'scale(0.94)' },
        onClick: (e, el) => el.call('hangUpWorkspaceCall')
      }
    }
  }
}
